import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Users as UsersIcon,
    Search,
    Filter,
    Calendar,
    Download,
    ArrowUpRight,
    ArrowDownRight,
    Mail,
    Tag,
    ChevronDown,
    ChevronRight,
    X,
    Check,
    AlertTriangle,
    Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

import AppHeader from '../components/layout/AppHeader';
import { useTimeRange } from '../hooks/useTimeRange';
import { cn } from '../lib/utils';
import UserFunnelChart from '../components/users/UserFunnelChart';
import AIFeaturesChart from '../components/users/AIFeaturesChart';
import UserMetricsCard from '../components/users/UserMetricsCard';
import { getEnhancedUserData, getDashboardMetrics, getUserFunnelData, getAIFeaturesData, getAdditionalUserMetrics } from '../services/analyticsService';
import { getTimeBasedGoals, getGoalComparison, sanitizeMetric } from '../lib/goalCalculations';
import { useNavigate } from 'react-router-dom';
import UserDetailPanel from './users/UserDetail';

const Users = () => {
    const { timeRange, formatDateRange } = useTimeRange();
    const [searchQuery, setSearchQuery] = useState('');

    // Convert timeRange string to number for API calls
    const getTimeRangeNumber = () => {
        const timeRangeMap = {
            'today': 1,
            '7d': 7,
            '14d': 14,
            '30d': 30,
            'month': 30,
            'quarter': 90,
            'year': 365,
            'all': 365
        };
        return timeRangeMap[timeRange] || 30;
    };
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserDetail, setShowUserDetail] = useState(false);
    const [users, setUsers] = useState([]);
    const [goals, setGoals] = useState(null);
    const [analytics, setAnalytics] = useState({
        totalUsers: { value: '0', change: '+0%', isPositive: true },
        newUsersWeekly: { value: '0', change: '+0%', isPositive: true },
        paidUsers: { value: '0', change: '+0%', isPositive: true },
        activeProSubscribers: { value: '0', change: '+0%', isPositive: true }
    });
    const [funnelData, setFunnelData] = useState({
        new: 0,
        engaged: 0,
        started: 0,
        saved: 0,
        paid: 0
    });
    const [aiFeaturesData, setAIFeaturesData] = useState({
        totalUsers: 0,
        aiAutofillTriggered: 0,
        aiResumeOptimizeApplied: 0,
        aiToolUsageRate: 0,
        totalAutofillEvents: 0,
        totalOptimizeEvents: 0
    });
    const [additionalMetrics, setAdditionalMetrics] = useState({
        anonymousPercentage: 0,
        deviceBreakdown: {
            browsers: [],
            operatingSystems: [],
            devices: []
        }
    });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const itemsPerPage = 10;

    // Fetch real data on component mount
    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (!isMounted) return;

            setLoading(true);
            try {
                const timeRangeNumber = getTimeRangeNumber();
                console.log('🔄 Starting data fetch for Users page...', { timeRange, timeRangeNumber });

                // Get goals for this time range
                const timeBasedGoals = getTimeBasedGoals(timeRangeNumber);
                setGoals(timeBasedGoals);

                const [dashboardMetrics, funnel, aiFeatures, userData, additionalMetrics] = await Promise.all([
                    getDashboardMetrics(timeRangeNumber),
                    getUserFunnelData(timeRangeNumber),
                    getAIFeaturesData(timeRangeNumber),
                    getEnhancedUserData(50), // Reduced from 100 to prevent excessive API calls
                    getAdditionalUserMetrics(timeRangeNumber)
                ]);

                if (!isMounted) return;

                // Calculate metrics according to README specifications with goal-based tracking
                const totalUsers = sanitizeMetric(dashboardMetrics.totalUsers);
                const paidUsers = sanitizeMetric(userData.filter(user => user.hasPaid).length);
                const activeProSubscribers = sanitizeMetric(userData.filter(user => user.isSubscribed).length);

                // Calculate new users in the last week
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                const newUsersWeekly = sanitizeMetric(userData.filter(user => {
                    const createdAt = user.createdAt?.toDate?.() || new Date(user.createdAt);
                    return createdAt >= oneWeekAgo;
                }).length);

                // Calculate goal-based comparisons
                const totalUsersComparison = getGoalComparison(
                    totalUsers,
                    0, // No previous data for total users
                    timeBasedGoals.users.total,
                    'totalUsers'
                );

                const newUsersComparison = getGoalComparison(
                    newUsersWeekly,
                    0, // No previous data for weekly users
                    timeBasedGoals.users.new,
                    'newUsers'
                );

                const paidUsersComparison = getGoalComparison(
                    paidUsers,
                    0, // No previous data for paid users
                    timeBasedGoals.users.paid,
                    'paidUsers'
                );

                const proSubscribersComparison = getGoalComparison(
                    activeProSubscribers,
                    0, // No previous data for pro subscribers
                    timeBasedGoals.users.pro,
                    'proSubscribers'
                );

                setAnalytics({
                    totalUsers: {
                        value: totalUsers.toLocaleString(),
                        change: `${totalUsersComparison.isPositive ? '+' : ''}${totalUsersComparison.change.toFixed(1)}%`,
                        isPositive: totalUsersComparison.isPositive
                    },
                    newUsersWeekly: {
                        value: newUsersWeekly.toLocaleString(),
                        change: `${newUsersComparison.isPositive ? '+' : ''}${newUsersComparison.change.toFixed(1)}%`,
                        isPositive: newUsersComparison.isPositive
                    },
                    paidUsers: {
                        value: paidUsers.toLocaleString(),
                        change: `${paidUsersComparison.isPositive ? '+' : ''}${paidUsersComparison.change.toFixed(1)}%`,
                        isPositive: paidUsersComparison.isPositive
                    },
                    activeProSubscribers: {
                        value: activeProSubscribers.toLocaleString(),
                        change: `${proSubscribersComparison.isPositive ? '+' : ''}${proSubscribersComparison.change.toFixed(1)}%`,
                        isPositive: proSubscribersComparison.isPositive
                    }
                });

                console.log('🔍 Funnel data received:', funnel);
                console.log('🔍 AI features data received:', aiFeatures);
                console.log('🔍 Additional metrics received:', additionalMetrics);
                console.log('🎯 Goals set:', timeBasedGoals);

                setFunnelData(funnel);
                setAIFeaturesData(aiFeatures);
                setAdditionalMetrics(additionalMetrics);
                setUsers(userData);
            } catch (error) {
                console.error('Error fetching users data:', error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [timeRange]);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const totalPages = Math.ceil(users.length / itemsPerPage);

    const getJourneyStageColor = (stage) => {
        switch (stage) {
            case 'paid':
                return 'bg-emerald-500/10 text-emerald-500';
            case 'saved':
                return 'bg-purple-500/10 text-purple-500';
            case 'started':
                return 'bg-yellow-500/10 text-yellow-500';
            case 'engaged':
                return 'bg-orange-500/10 text-orange-500';
            case 'new':
                return 'bg-gray-500/10 text-gray-500';
            default:
                return 'bg-red-500/10 text-red-500';
        }
    };

    const handleCardClick = (route) => {
        navigate(route);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Filter users based on search query
    const filteredUsers = users.filter(user => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            user.name?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query) ||
            user.tags?.some(tag => tag.toLowerCase().includes(query))
        );
    });

    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <AppHeader
                title="Users"
                icon={UsersIcon}
                searchPlaceholder="Search users..."
                onSearch={setSearchQuery}
                timeRange={timeRange}
                onTimeRangeChange={() => { }} // Time range is managed by context
            />

            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center space-y-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                            <p className="text-muted-foreground">Loading user data...</p>
                        </div>
                    </div>
                )}

                {!loading && (
                    <>
                        {/* Summary Cards */}
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <Card
                                className="relative overflow-hidden bg-card hover:bg-card/90 transition-all duration-300 border border-border rounded-xl shadow-sm hover:shadow-md cursor-pointer"
                                onClick={() => handleCardClick('/users/insights')}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-foreground">Total Users</CardTitle>
                                    <UsersIcon className="h-4 w-4 text-primary/50 group-hover:text-primary/80" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold text-foreground">{analytics.totalUsers.value}</div>
                                    <div className="flex items-center text-xs mt-1">
                                        <span className={cn(
                                            "flex items-center",
                                            analytics.totalUsers.isPositive ? "text-emerald-500" : "text-red-500"
                                        )}>
                                            {analytics.totalUsers.isPositive ? (
                                                <ArrowUpRight className="h-3 w-3 mr-1" />
                                            ) : (
                                                <ArrowDownRight className="h-3 w-3 mr-1" />
                                            )}
                                            {analytics.totalUsers.change}
                                        </span>
                                        <span className="text-muted-foreground ml-1">vs goal</span>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card
                                className="relative overflow-hidden bg-card hover:bg-card/90 transition-all duration-300 border border-border rounded-xl shadow-sm hover:shadow-md cursor-pointer"
                                onClick={() => handleCardClick('/users/insights')}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-foreground">New Users Weekly</CardTitle>
                                    <UsersIcon className="h-4 w-4 text-primary/50 group-hover:text-primary/80" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold text-foreground">{analytics.newUsersWeekly.value}</div>
                                    <div className="flex items-center text-xs mt-1">
                                        <span className={cn(
                                            "flex items-center",
                                            analytics.newUsersWeekly.isPositive ? "text-emerald-500" : "text-red-500"
                                        )}>
                                            {analytics.newUsersWeekly.isPositive ? (
                                                <ArrowUpRight className="h-3 w-3 mr-1" />
                                            ) : (
                                                <ArrowDownRight className="h-3 w-3 mr-1" />
                                            )}
                                            {analytics.newUsersWeekly.change}
                                        </span>
                                        <span className="text-muted-foreground ml-1">vs goal</span>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card
                                className="relative overflow-hidden bg-card hover:bg-card/90 transition-all duration-300 border border-border rounded-xl shadow-sm hover:shadow-md cursor-pointer"
                                onClick={() => handleCardClick('/users/insights')}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-foreground">Paid Users</CardTitle>
                                    <UsersIcon className="h-4 w-4 text-primary/50 group-hover:text-primary/80" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold text-foreground">{analytics.paidUsers.value}</div>
                                    <div className="flex items-center text-xs mt-1">
                                        <span className={cn(
                                            "flex items-center",
                                            analytics.paidUsers.isPositive ? "text-emerald-500" : "text-red-500"
                                        )}>
                                            {analytics.paidUsers.isPositive ? (
                                                <ArrowUpRight className="h-3 w-3 mr-1" />
                                            ) : (
                                                <ArrowDownRight className="h-3 w-3 mr-1" />
                                            )}
                                            {analytics.paidUsers.change}
                                        </span>
                                        <span className="text-muted-foreground ml-1">vs goal</span>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card
                                className="relative overflow-hidden bg-card hover:bg-card/90 transition-all duration-300 border border-border rounded-xl shadow-sm hover:shadow-md cursor-pointer"
                                onClick={() => handleCardClick('/users/insights')}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-foreground">Active Pro Subscribers</CardTitle>
                                    <UsersIcon className="h-4 w-4 text-primary/50 group-hover:text-primary/80" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold text-foreground">{analytics.activeProSubscribers.value}</div>
                                    <div className="flex items-center text-xs mt-1">
                                        <span className={cn(
                                            "flex items-center",
                                            analytics.activeProSubscribers.isPositive ? "text-emerald-500" : "text-red-500"
                                        )}>
                                            {analytics.activeProSubscribers.isPositive ? (
                                                <ArrowUpRight className="h-3 w-3 mr-1" />
                                            ) : (
                                                <ArrowDownRight className="h-3 w-3 mr-1" />
                                            )}
                                            {analytics.activeProSubscribers.change}
                                        </span>
                                        <span className="text-muted-foreground ml-1">vs goal</span>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Charts and Metrics */}
                        <div className="grid gap-6 md:grid-cols-3">
                            <div className="md:col-span-1">
                                <UserFunnelChart data={funnelData} />
                            </div>
                            <div className="md:col-span-1">
                                <AIFeaturesChart data={aiFeaturesData} />
                            </div>
                            <div className="md:col-span-1">
                                <UserMetricsCard
                                    data={additionalMetrics}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* User Detail Side Panel - Removed to prevent infinite loop */}
            <UserDetailPanel
                user={selectedUser}
                isOpen={showUserDetail}
                onClose={() => {
                    setShowUserDetail(false);
                    setSelectedUser(null);
                }}
            />
        </div>
    );
};

export default Users; 